import { ReactNode, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    FormControl,
    FormLabel,
    Grid,
    IconButton,
    Input,
    Stack,
    Typography,
} from '@mui/joy';
import { Add, Delete } from '@mui/icons-material';
import { PageStandard } from '../utils/PageStandard';
import { formatCurrency } from '../utils/Utils';
import { calculateQuotaGc, QUOTA_GC_RATES as RATES } from '../utils/QuotaGcCalculator';

function toNumber(value: string): number {
    const n = parseFloat(value.replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
}

export function CalcoloQuotaGc() {
    const [participants, setParticipants] = useState('35');
    const [gcFee, setGcFee] = useState('6');
    const [venueFees, setVenueFees] = useState<string[]>(['5', '10', '5', '5']);

    const result = useMemo(() => calculateQuotaGc({
        participants: toNumber(participants),
        gcFeePerPerson: toNumber(gcFee),
        venueFeesPerPerson: venueFees.map(toNumber),
    }), [participants, gcFee, venueFees]);

    function updateVenue(index: number, value: string) {
        setVenueFees(prev => prev.map((v, i) => (i === index ? value : v)));
    }

    function removeVenue(index: number) {
        setVenueFees(prev => prev.filter((_, i) => i !== index));
    }

    return (
        <PageStandard title="Calcolo quota GC" sidebarVisible>
            <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid xs={12} md={5}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography level="title-lg" sx={{ mb: 1 }}>Dati esperienza</Typography>
                            <Stack spacing={2}>
                                <FormControl>
                                    <FormLabel>Numero partecipanti</FormLabel>
                                    <Input
                                        type="number"
                                        value={participants}
                                        onChange={e => setParticipants(e.target.value)}
                                        slotProps={{ input: { min: 0, step: 1 } }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Quota base GoCicero €/persona</FormLabel>
                                    <Input
                                        type="number"
                                        value={gcFee}
                                        onChange={e => setGcFee(e.target.value)}
                                        endDecorator="€"
                                        slotProps={{ input: { min: 0, step: 0.5 } }}
                                    />
                                </FormControl>

                                <Box>
                                    <FormLabel sx={{ mb: 1 }}>Quota per locale €/persona</FormLabel>
                                    <Stack spacing={1}>
                                        {venueFees.map((fee, i) => (
                                            <Stack key={i} direction="row" spacing={1} alignItems="center">
                                                <Typography level="body-sm" sx={{ minWidth: 64 }}>
                                                    Locale {i + 1}
                                                </Typography>
                                                <Input
                                                    type="number"
                                                    value={fee}
                                                    onChange={e => updateVenue(i, e.target.value)}
                                                    endDecorator="€"
                                                    sx={{ flex: 1 }}
                                                    slotProps={{ input: { min: 0, step: 0.5 } }}
                                                />
                                                <IconButton
                                                    color="danger"
                                                    variant="plain"
                                                    onClick={() => removeVenue(i)}
                                                    aria-label={`Rimuovi locale ${i + 1}`}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </Stack>
                                        ))}
                                    </Stack>
                                    <Button
                                        variant="soft"
                                        startDecorator={<Add />}
                                        onClick={() => setVenueFees(prev => [...prev, ''])}
                                        sx={{ mt: 1 }}
                                    >
                                        Aggiungi locale
                                    </Button>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Typography level="body-xs" color="neutral" sx={{ mt: 1.5 }}>
                        Dati fissi: carta {formatPercent(RATES.cardFeePercent)} + {formatCurrency(RATES.cardFeeFixed)} (1 pagamento),
                        Connect routing {formatPercent(RATES.connectRoutingPercent)} + payout {formatPercent(RATES.connectPayoutPercent)} + {formatCurrency(RATES.connectPayoutFixed)}/payout,
                        {' '}{formatCurrency(RATES.connectAccountMonthly)}/account attivo, aliquota fiscale {formatPercent(RATES.taxRate)},
                        aliquota inversa 30/70, marca da bollo {formatCurrency(RATES.stampDuty)} se la commissione GoCicero lorda supera {formatCurrency(RATES.stampDutyThreshold)}. Locali, payout e account attivi = locali con quota maggiore di 0.
                    </Typography>
                </Grid>

                <Grid xs={12} md={7}>
                    <Stack spacing={3}>
                        <ResultGroup title="Prima del sovrapprezzo">
                            <ResultCard
                                label="Prezzo base cliente"
                                value={result.basePrice}
                                rows={[
                                    { label: 'Quota locali €/persona', value: result.venuesFeePerPerson },
                                    { label: 'Quota GoCicero €/persona', value: result.gcFeePerPerson },
                                    { label: 'Prezzo base €/persona', value: result.basePricePerPerson, strong: true },
                                    { label: `Totale ai locali (${result.participants} persone)`, value: result.totalToVenues },
                                    { label: 'Commissione GoCicero base', value: result.gcBaseCommission },
                                ]}
                            />
                            <ResultCard
                                label="Totale costi Connect"
                                value={result.totalConnectCosts}
                                rows={[
                                    { label: `Routing / management ${formatPercent(RATES.connectRoutingPercent)}`, value: result.connectRouting },
                                    { label: `Payout ${formatPercent(RATES.connectPayoutPercent)}`, value: result.connectPayoutPercent },
                                    { label: `Payout fissi (${result.activeVenues} × ${formatCurrency(RATES.connectPayoutFixed)})`, value: result.connectPayoutFixed },
                                    { label: `Account attivi (${result.activeVenues} × ${formatCurrency(RATES.connectAccountMonthly)})`, value: result.connectAccounts },
                                ]}
                            />
                            <ResultCard
                                label="Netto GoCicero target"
                                caption="Senza commissioni Stripe"
                                value={result.netGoCiceroTarget}
                                rows={[
                                    { label: 'Commissione GoCicero base', value: result.gcBaseCommission },
                                    { label: `Tasse ${formatPercent(RATES.taxRate)}`, value: -result.gcBaseCommission * RATES.taxRate },
                                ]}
                            />
                        </ResultGroup>

                        <ResultGroup title="Dopo il sovrapprezzo">
                            <ResultCard
                                label="Sovrapprezzo necessario"
                                caption="Per mantenere il netto GoCicero target"
                                value={result.surcharge}
                                rows={[
                                    { label: 'Sovrapprezzo €/persona', value: result.surchargePerPerson },
                                    { label: stampDutyLabel(result.stampDuty), value: result.stampDuty },
                                ]}
                            />
                            <ResultCard
                                label="Totale pagato dal cliente"
                                value={result.totalCustomerPrice}
                                rows={[
                                    { label: 'Prezzo base', value: result.basePrice },
                                    { label: 'Sovrapprezzo', value: result.surcharge },
                                    { label: 'Prezzo finale €/persona', value: result.customerPricePerPerson, strong: true },
                                ]}
                            />
                            <ResultCard
                                label="Totale incassato da Stripe come fee"
                                value={result.totalStripeFees}
                                rows={[
                                    { label: `Fee carta ${formatPercent(RATES.cardFeePercent)} sul totale cliente`, value: result.cardFeePercent },
                                    { label: `Fee carta fissa (${RATES.stripePayments} pagamento)`, value: result.cardFeeFixed },
                                    { label: 'Totale costi Connect', value: result.totalConnectCosts },
                                ]}
                            />
                            <ResultCard
                                label="Netto GoCicero dopo tasse e Stripe"
                                value={result.netGoCicero}
                                highlight
                                rows={[
                                    { label: 'Commissione GoCicero lorda (base + sovrapprezzo)', value: result.gcGross },
                                    { label: 'Fee Stripe', value: -result.totalStripeFees },
                                    { label: stampDutyLabel(result.stampDuty), value: -result.stampDuty },
                                    { label: 'Netto prima delle tasse', value: result.netBeforeTaxes, strong: true },
                                    { label: `Tasse ${formatPercent(RATES.taxRate)} sulla commissione lorda`, value: -result.taxes },
                                    { label: 'Differenza vs target', value: result.netGoCicero - result.netGoCiceroTarget },
                                ]}
                            />
                        </ResultGroup>
                    </Stack>
                </Grid>
            </Grid>
        </PageStandard>
    );
}

function formatPercent(rate: number): string {
    return `${(rate * 100).toLocaleString('it-IT', { maximumFractionDigits: 2 })}%`;
}

function stampDutyLabel(stampDuty: number): string {
    return stampDuty > 0
        ? 'Marca da bollo (inclusa)'
        : `Marca da bollo (non dovuta, lordo GC fino a ${formatCurrency(RATES.stampDutyThreshold)})`;
}

function ResultGroup(props: { title: string; children: ReactNode }) {
    return (
        <Box>
            <Typography level="title-md" sx={{ mb: 1 }}>{props.title}</Typography>
            <Grid container spacing={2}>{props.children}</Grid>
        </Box>
    );
}

interface ResultRow {
    label: string;
    value: number;
    strong?: boolean;
}

function ResultCard(props: { label: string; value: number; caption?: string; rows: ResultRow[]; highlight?: boolean }) {
    return (
        <Grid xs={12} sm={6}>
            <Card
                variant={props.highlight ? 'solid' : 'soft'}
                color={props.highlight ? 'primary' : 'neutral'}
                invertedColors={props.highlight}
                sx={{ height: '100%' }}
            >
                <CardContent>
                    <Typography level="body-sm" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {props.label}
                    </Typography>
                    <Typography level="h2" sx={{ mt: 0.5 }}>
                        {formatCurrency(props.value)}
                    </Typography>
                    {props.caption && (
                        <Typography level="body-xs">{props.caption}</Typography>
                    )}
                    <Divider sx={{ my: 1.5 }} />
                    <Stack spacing={0.5}>
                        {props.rows.map(row => (
                            <Stack key={row.label} direction="row" justifyContent="space-between" spacing={2}>
                                <Typography level="body-sm" fontWeight={row.strong ? 'lg' : undefined}>
                                    {row.label}
                                </Typography>
                                <Typography
                                    level="body-sm"
                                    fontWeight={row.strong ? 'lg' : undefined}
                                    sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                                >
                                    {formatCurrency(row.value)}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </CardContent>
            </Card>
        </Grid>
    );
}
